<template>
  <div
    v-click-outside="transitionToIdle"
    data-sb-regression="sb-dropdown-search"
    class="searchable-input"
    :class="{ disabled }"
  >
    <template v-if="hasSelectionScopedSlot && !showCurrentSelection">
      <div
        v-show="hasIdleSelectionState"
        class="full-height-width-center"
        @click="transitionToFocused"
      >
        <!--
          @slot slot to show selection value
        -->
        <slot
          name="selection"
          :selection="value"
        >
          {{ value }}
        </slot>
      </div>
    </template>
    <SbTextInput
      v-show="showCurrentSelection || !hasIdleSelectionState"
      ref="textInput"
      v-model:value="searchTerm"
      autocomplete="off"
      v-bind="getAttributes()"
      :state="textInputState"
      class="full-height-width-center ds-search-input"
      @keyup="handleEscape"
      @focus="transitionToFocused"
      @set-label-for="setLabelFor"
    >
      <template #prepend>
        <i
          aria-hidden="true"
          class="sb-icon-search"
        />
      </template>
    </SbTextInput>
    <SbPopover
      :target="popoverTarget"
      :active="hasSelectingState"
      placement="center"
      orientation="bottom"
      :boundaries-element="boundariesElement"
    >
      <FadeTransition :duration="fadeDuration">
        <div
          v-if="hasSelectingState"
          class="options"
        >
          <template v-if="hasSelectionOptions">
            <a
              v-for="(option, index) in options"
              :key="computeKey(index, option)"
              href="#"
              :class="optionClasses(option)"
              @click.prevent="selectOption(option)"
            >
              <template v-if="isArray">
                <i
                  v-if="optionSelected(option)"
                  aria-hidden="true"
                  class="option__multi-icon sb-icon-check_box"
                />
                <i
                  v-else
                  aria-hidden="true"
                  class="option__multi-icon sb-icon-check_box_outline_blank"
                />
              </template>
              <!--
                @slot slow to show the option
              -->
              <slot
                :option="option"
                :index="index"
              >
                {{ option }}
              </slot>
            </a>
          </template>
          <div
            v-if="!hasSelectionOptions"
            class="option empty"
          >
            <!--
              @slot slot to show text when there are no options
            -->
            <slot name="empty-options">
              <sb-text :value="emptyOptionsText" />
            </slot>
          </div>
          <FadeTransition :duration="100">
            <SbLoadingSpinner
              v-if="loading"
              :size="'xtsm'"
            />
          </FadeTransition>
        </div>
      </FadeTransition>
    </SbPopover>
  </div>
  <div
    v-if="showCurrentSelection"
    class="selection-section"
  >
    <template v-if="selectedOptions?.length > 0">
      <sb-pill-selection
        :selection="selectedOptions"
        @removePill="removeOption"
      />
    </template>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';

import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import SbLoadingSpinner from '@src/components/Loading/SbLoadingSpinner.vue';
import SbTextInput from '@src/components/Input/SbTextInput.vue';
import SbText from '@src/components/Text/SbText.vue';

import DeprecationTrackingMixin from '@src/utils/mixins/deprecatedComponentTracking';
import labelHelperMixin from '@src/utils/mixins/labelHelperMixin';
import { InputMachine } from '@src/components/Dropdown/classes/InputMachine';
import SbPillSelection from '@src/components/Dropdown/components/SbPillSelection.vue';

/**
 * Dropdown search is a component used to lookup items and make a selection on them.
 * the selection can be bound in your parent component using the `v-model` syntax.
 * If the bound model is of type `Array`, you will be able to make multiple selections.
 * Otherwise the selection will be a single element.
 */
export default {
  name: 'SbDropdownSearch',
  components: {
    SbPopover,
    FadeTransition,
    SbLoadingSpinner,
    SbTextInput,
    SbText,
    SbPillSelection,
  },
  mixins: [labelHelperMixin, ClickOutsideDirectiveMixin, DeprecationTrackingMixin],
  props: {
    value: {},

    /**
     * If your options list is an object, this value is used to select the attribute on that object to be used as the key in the list.
     * https://vuejs.org/v2/guide/list.html
     * The fallback for key is the index of the item in the list.
     */
    keyValue: {
      type: String,
      default: 'key',
    },
    /**
     * This is the list of items to be displayed in the dropdown.
     * If your options list contains a list of objects, you can use the scoped-slot to access the object during the list rendering.
     * https://vuejs.org/v2/guide/components-slots.html#Scoped-Slots
     * This allows you to determine how to structure the markup in the option.
     * you also have access to the index of the item in the list as part of the slot-scope.
     * Ex: `<span slot-scope="{ option, index }">{{ index }} {{ option | i18 }}</span>`
     */
    options: {
      type: Array,
      default: () => ([]),
    },
    /**
     * This is a time in ms that will be used as a timeout before emitting the search event.
     */
    debounce: {
      type: Number,
      default: 500,
    },
    /**
     * This is the length of the string that must be reached before emitting the search event.
     */
    minSearchTermLength: {
      type: Number,
      default: 3,
    },
    /**
     * This is the text to be used if the options list is empty.
     * If you need control over the markup of the empty options, you can use the `empty-options` slot.
     * When using this, it will override the text value with that markup.
     */
    emptyOptionsText: {
      type: String,
      default: 'sbDropdown.noResults',
    },
    /**
     * This property determines whether or not to show the selected option when the search input is not focused and the dropdown is not open.
     * If you would like to change the markup of the selected option, you can use the named slot `selection`. The slot-scope also exposes the current selection bound by v-model.
     * you can access it like this:
     * `<span #selection="{ selection }">{{ selection | i18 }}</span>`
     */
    showSelectedOptions: {
      type: Boolean,
      default: false,
    },
    /**
     * Toggles on/off the loader in the dropdown.
     * Use this if your options list is an async operation.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Clears the search input after a selection is made from the list.
     */
    clearSearchOnSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable dropdown from user input
    */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Compare function to equate options with the selected options. Defaults to isEqual from lodash
     */
    comparator: {
      type: Function,
      default: isEqual,
    },
    /**
     * Boundaries of the containing element
     */

    boundariesElement: {
      default: 'viewport',
    },
    /**
     * Show current selection with dismissable pills at the bottom
     */

    showCurrentSelection: {
      default: false,
      type: Boolean,
    },

  },
  emits: [
    /**
     * When search term changes, this action is emitted with the new and previous search term
     */
    'search',
    /**
     * Emitted when the selected options change within the dropdown
     */
    'update:value',
    /**
     * Emitted when a label needs to be set for a specific id
     */
    'set-label-for',
  ],
  data: () => ({
    machine: null,
    search: null,
    searchTerm: '',
    textInputState: null,
    selectedOptions: [],
  }),
  computed: {
    hasSelectionScopedSlot() {
      return this.$slots.selection && this.$slots.selection();
    },
    fadeDuration() {
      return 100;
    },
    popoverTarget() {
      return this.$refs.textInput;
    },
    idleState() {
      return this.shouldShowSelectedOptions ? 'Idle.Selection' : 'Idle';
    },
    hasIdleState() {
      return this.machine && (this.machine.state === 'Idle.Selection' || this.machine.state === 'Idle');
    },
    hasIdleSelectionState() {
      return this.machine && this.machine.state === 'Idle.Selection';
    },
    hasSelectingState() {
      return this.machine && this.machine.state.indexOf('Selecting') > -1;
    },
    minSearchTermMet() {
      return this.searchTerm?.length >= this.minSearchTermLength;
    },
    hasSelectionOptions() {
      return this.options && this.options.length > 0;
    },
    isArray() {
      return Array.isArray(this.value);
    },
    shouldShowSelectedOptions() {
      return (this.$slots.selection && this.$slots.selection())
        || this.showSelectedOptions;
    },
    inputEl() {
      const inputRef = this.$refs.textInput.$el;
      return inputRef.tagName.toLowerCase() === 'input' ? inputRef : inputRef.querySelector('input');
    },
  },
  watch: {
    searchTerm(searchTerm, previousTerm) {
      this.search({ searchTerm, previousTerm });
    },
    value(newValue) {
      if (Array.isArray(newValue)) {
        this.selectedOptions = [...newValue];
      } else {
        this.selectedOptions = newValue ? [newValue] : null;
      }
    },
    hasIdleSelectionState() {
      if (this.showCurrentSelection) {
        this.searchTerm = '';
      }
    },
  },
  mounted() {
    this.machine = new InputMachine(this.idleState, this);
    this.search = debounce(this.searchFunc, this.debounce);
    if (Array.isArray(this.value)) {
      this.selectedOptions = [...this.value];
    } else {
      this.selectedOptions = this.value ? [this.value] : null;
    }
  },
  methods: {
    getAttributes() {
      const attributes = { ...this.$attrs };
      if (this.disabled) {
        attributes.disabled = true;
      }
      return attributes;
    },
    resetFilters() {
      this.$emit('update:value', []);
      this.selectedOptions = [];
    },
    removeOption(option) {
      if (Array.isArray(this.value)) {
        this.emitNewSelectionOptions(option);
      } else {
        this.emitNewSelectionOption(null);
      }
    },
    handleEscape(e) {
      if (e.keyCode === 27) {
        e.stopPropagation();
        this.transitionToIdle();
      }
    },
    transitionToIdle() {
      this.inputEl.blur();
      this.machine.changeStateTo(this.idleState);
      this.textInputState = null;
    },
    transitionToFocused() {
      if (this.disabled) return;
      this.machine.dispatch('focus', {
        inputEl: this.inputEl,
        openSelectionImmediately: this.minSearchTermMet,
      });
      this.textInputState = 'focus';
    },
    searchFunc({ searchTerm, previousTerm }) {
      if (this.minSearchTermMet) this.machine.dispatch('select');
      else this.machine.dispatch('deselect');
      if (this.hasSelectingState) this.$emit('search', { searchTerm, previousTerm });
    },
    computeKey(index, option) {
      return option[this.keyValue] || index;
    },
    optionClasses(option) {
      const classes = ['option'];
      if (this.isArray) classes.push('multi');
      if ((this.isArray && this.optionSelected(option)) || this.value === option) classes.push('selected');
      return classes;
    },
    optionSelected(option) {
      return this.value.some((v) => this.comparator(v, option));
    },
    emitNewSelectionOptions(option) {
      const arrCopy = this.value.slice();
      if (arrCopy.some((v) => this.comparator(v, option))) {
        arrCopy.splice(arrCopy.findIndex((v) => this.comparator(v, option)), 1);
      } else {
        arrCopy.push(option);
      }
      this.$emit('update:value', arrCopy);
      this.selectedOptions = arrCopy;
    },
    emitNewSelectionOption(option) {
      this.$emit('update:value', option);
      this.selectedOptions = option ? [option] : null;
    },
    selectOption(option) {
      if (this.isArray) {
        this.emitNewSelectionOptions(option);
        this.$refs.textInput.$el.querySelector('input').focus();
      } else {
        this.emitNewSelectionOption(option);
        this.transitionToIdle();
      }
      if (this.clearSearchOnSelect) this.searchTerm = '';
    },
  },
};
</script>

<style lang="scss">
@import '@src/components/Dropdown/style/sb-dropdown-search.scss';
</style>
